/**
 * The editor that opens on one cell — `KLQ-20260828-001` §B, §D.
 *
 * One element at a time, and it is deliberately small: a text box, a date box, or a picker.
 * Everything that decides *what a value means* lives on the column (`edit.parse`), and
 * everything that decides *which rows this reaches* lives in the table — this file only has
 * to take input, say how many rows are about to change, and hand back a string.
 *
 * **A string, always.** Typing and pasting are the same act one keystroke apart, so both go
 * through the column's `parse()` rather than each growing its own conversion. It is also what
 * lets a date cell accept `2026-09-30` from a sheet and from a keyboard without caring which
 * one it was.
 *
 * **THE GEOMETRY RULE, ADDED 2026-08-28: EDITING NEVER MOVES THE TABLE.**
 *
 * The first implementation put the editor *in* the cell, in the normal flow, and reasoned that
 * this was the way to keep the column still. It is the opposite. A bordered input is taller
 * than the text it replaced, and a native `<select multiple size=5>` is a list box with a real
 * height — so opening a picker grew the row, and the fan-out count beside it grew the column.
 * The person choosing an assignee watched the table move under the pointer.
 *
 * So the editor is now a **layer over the cell**, never a participant in its layout:
 *
 * - a text or date box is absolutely positioned over the cell (`inset: 0`) and may spill to
 *   the right over its neighbour, the way a spreadsheet's edit box does, because a value
 *   longer than the column is not a reason to reflow the column;
 * - a picker draws a quiet trigger in the cell's own footprint and puts its list in a
 *   portalled `ListboxPopup` — one component, shared with whatever needs a list next
 *   (`components/ListboxPopup.tsx`), not a second dialect grown in the grid;
 * - the count, and the question above the threshold, ride in the popup's footer or in the
 *   overlay — never in a flex row beside the field, which is what used to widen the column.
 *
 * The rule to keep: **nothing this file renders may contribute to the intrinsic size of a
 * `<td>`.** If a change here would, it is the wrong change.
 */

import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ListboxPopup } from "@/components/ListboxPopup";
import { useMessages } from "@/lib/i18n";

import styles from "./TreeTable.module.css";

export interface CellEditorOption {
  readonly id: string;
  readonly label: string;
}

/** A value waiting for the answer to §D.2's question. */
interface Pending {
  readonly value: string;
  readonly andMove: "down" | "right" | null;
}

export function CellEditor({
  kind,
  label,
  options,
  initial,
  typed,
  count,
  reach,
  confirmAbove,
  onCommit,
  onCancel,
}: {
  readonly kind: "text" | "date" | "select" | "multiselect";
  /** The column's own header — the picker's accessible name. No copy is invented here. */
  readonly label: string;
  readonly options: readonly CellEditorOption[];
  readonly initial: string;
  /** The printable character that opened the editor, if one did: it seeds a text box's
      contents and a picker's search, rather than being swallowed by the opening. */
  readonly typed?: string | undefined;
  /** How many rows this edit will reach. Shown whenever it is more than the one in front. */
  readonly count: number;
  /**
   * How many *further* rows will display the value without being written — `KLQ-20260828-003`
   * §F.2.
   *
   * The write count is the honest number for a write and a misleading number for an effect:
   * a responsible set on three buildings writes three rows and changes what four hundred show.
   * A column whose value nothing inherits leaves this `undefined` and the editor is unchanged.
   */
  readonly reach?: number | undefined;
  /** Above this, the editor asks once before writing (§D.2). */
  readonly confirmAbove: number;
  readonly onCommit: (input: string, andMove: "down" | "right" | null) => void;
  readonly onCancel: () => void;
}) {
  const t = useMessages("app");
  const [draft, setDraft] = useState(initial);
  const [asking, setAsking] = useState<Pending | null>(null);
  const field = useRef<HTMLInputElement>(null);
  const apply = useRef<HTMLButtonElement>(null);
  /** The trigger, once it is in the DOM — what the popup hangs off. State and not a ref
      because the popup has to re-render when it arrives, and a ref does not say so. */
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const many = kind === "multiselect";
  const picking = kind === "select" || many;
  const held = many ? draft.split(",").filter(Boolean) : draft === "" ? [] : [draft];

  useEffect(() => {
    if (picking) return;
    field.current?.focus();
    field.current?.select();
  }, [picking]);

  // The question takes focus so that `Enter` answers it. Nothing else is worth pressing while
  // it stands, and a confirmation nobody's keyboard can reach is a confirmation by mouse only.
  useEffect(() => {
    if (asking !== null) apply.current?.focus();
  }, [asking]);

  /**
   * A big fan-out is undoable, but an undo that has to be *noticed* first is not a safety net.
   * Below the threshold the undo stack is the safety net and nothing interrupts.
   *
   * The value travels with the question rather than being read back out of `draft`: a single
   * picker answers and commits in one act, and `draft` is a render behind at that moment.
   */
  const send = (value: string, andMove: "down" | "right" | null): void => {
    if (count > confirmAbove) {
      setAsking({ value, andMove });
      return;
    }
    onCommit(value, andMove);
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    // The table above listens for the same keys; while an editor is open they belong here.
    event.stopPropagation();
    if (asking !== null) {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel();
      }
      if (event.key === "Enter") {
        event.preventDefault();
        onCommit(asking.value, asking.andMove);
      }
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel();
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      send(draft, "down");
      return;
    }
    if (event.key === "Tab") {
      event.preventDefault();
      send(draft, "right");
    }
  };

  /**
   * What is about to happen, in numbers — `KLQ-20260828-003` §F.2.
   *
   * **Two numbers when they differ, and they mean different things.** The first counts rows
   * this *writes*; the second counts rows that will *show* the value without being written,
   * because they inherit it. Stating only the first is honest about the write and silent about
   * the effect — *"3 rows"* for three buildings whose four hundred descendants all change what
   * they display. Stating only a combined total would be worse: it would promise an undo over
   * rows nothing touched.
   *
   * A column nothing inherits from passes no `reach`, and this is the single count it always
   * was.
   */
  const tally = (
    <>
      <span>{t("treeTable.editCount", { count })}</span>
      {reach !== undefined && reach > 0 ? (
        <span className={styles.editorReach}>
          {t("treeTable.editReach", { count: reach })}
        </span>
      ) : null}
    </>
  );

  /** Whether either number is worth printing at all. */
  const worthSaying = count > 1 || (reach ?? 0) > 0;

  /** The same words as the bulk actions and as `Delete` — one question, three doors. */
  const question = (pending: Pending) => (
    <div className={styles.editorConfirm}>
      {tally}
      <button
        ref={apply}
        type="button"
        onClick={() => {
          onCommit(pending.value, pending.andMove);
        }}
      >
        {t("treeTable.editApply")}
      </button>
      <button type="button" onClick={onCancel}>
        {t("treeTable.editCancel")}
      </button>
    </div>
  );

  if (asking !== null) {
    // The list is gone by now: a question with a live list behind it invites a second pick
    // that the question does not describe.
    return (
      <div className={styles.editor} onKeyDown={onKeyDown}>
        {question(asking)}
      </div>
    );
  }

  if (picking) {
    const chosen = held
      .map((id) => options.find((one) => one.id === id)?.label)
      .filter((one): one is string => one !== undefined);
    return (
      <div
        className={[styles.editor, styles.editorPicking].join(" ")}
        onKeyDown={onKeyDown}
      >
        {/* The cell's own footprint, held by something focusable, so the cell keeps its size
            and the popup has a box to hang off. */}
        <button
          ref={setAnchor}
          type="button"
          className={styles.editorTrigger}
          aria-haspopup="listbox"
          aria-expanded
          aria-label={label}
          onClick={() => {
            send(draft, null);
          }}
        >
          <span className={styles.editorTriggerLabel}>{chosen.join(", ")}</span>
          <ChevronDown size={14} strokeWidth={1.5} aria-hidden />
        </button>

        <ListboxPopup
          label={label}
          anchor={anchor}
          options={options}
          selected={held}
          multiple={many}
          searchLabel={t("treeTable.pickerSearch")}
          emptyLabel={t("treeTable.pickerEmpty")}
          // Single-select says "nobody" with a row; a multiple one says it by holding none.
          noneLabel={many ? undefined : t("treeTable.pickerNone")}
          initialQuery={typed}
          onChoose={(id, andMove) => {
            if (!many) {
              // Picking *replaces* (§C.4) and finishes in one act — which is why the move
              // arrives with the id instead of following it: reading `draft` back here would
              // read the value the person just replaced.
              setDraft(id);
              send(id, andMove);
              return;
            }
            setDraft(
              (held.includes(id)
                ? held.filter((one) => one !== id)
                : [...held, id]
              ).join(","),
            );
          }}
          onDone={(andMove) => {
            send(draft, andMove);
          }}
          onCancel={onCancel}
          footer={worthSaying ? tally : undefined}
        />
      </div>
    );
  }

  return (
    <div className={styles.editor} onKeyDown={onKeyDown}>
      <input
        ref={field}
        // `size=1` keeps the box's intrinsic width tiny, so the overlay is as wide as the
        // cell and no wider — until the count needs room, which is the one case where
        // spilling over the neighbour beats squeezing the field.
        size={1}
        className={styles.editorField}
        type={kind === "date" ? "date" : "text"}
        value={draft}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        // Committing on blur is what "a click elsewhere commits" means; the confirmation step
        // is the one case where the editor must stay open to be answered.
        onBlur={() => {
          send(draft, null);
        }}
      />
      {worthSaying ? (
        // The count sits with the editor rather than in a dialog, the way a bulk action puts
        // it in its own label: what is about to happen is stated where the person is looking.
        <span className={styles.editorCount}>{tally}</span>
      ) : null}
    </div>
  );
}
